import { useCallback, useEffect, useMemo, useState } from "react";
import {
  farmLocalIsoDate,
  jalaliFarmDate,
  loadFeedingPolicyAdministration,
  type FeedingPolicyAdministrationSnapshot,
} from "../feeding/feedingPolicyAdministration";
import {
  buildFeedingDayRebuildOperation,
  buildFeedingPolicyPublishOperation,
} from "../operations/operationalPolicyOperations";
import { supabase } from "../auth/supabaseClient";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface FeedingPolicyAdministrationPanelProps {
  farmId: string;
  userId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

const WEEKDAYS = [
  { value: 6, label: "شنبه" },
  { value: 0, label: "یکشنبه" },
  { value: 1, label: "دوشنبه" },
  { value: 2, label: "سه‌شنبه" },
  { value: 3, label: "چهارشنبه" },
  { value: 4, label: "پنجشنبه" },
  { value: 5, label: "جمعه" },
] as const;

function syncMessage(status: string): string {
  if (status === "synced") return "نسخه جدید برنامه تغذیه منتشر و همگام شد.";
  if (status === "pending") return "نسخه جدید در صف آفلاین ثبت شد.";
  if (status === "retryable_error") return "تغییر حفظ شد و دوباره برای سرور ارسال می‌شود.";
  if (status === "terminal_conflict") return "انتشار نیاز به بررسی تعارض دارد.";
  return "تغییر برنامه تغذیه ثبت شد.";
}

export function FeedingPolicyAdministrationPanel({
  farmId,
  userId,
  onSyncSummary,
}: FeedingPolicyAdministrationPanelProps) {
  const [snapshot, setSnapshot] = useState<FeedingPolicyAdministrationSnapshot>();
  const [policyName, setPolicyName] = useState("");
  const [activeWeekdays, setActiveWeekdays] = useState<number[]>([]);
  const [completionTargetMinutes, setCompletionTargetMinutes] = useState(30);
  const [minimumGapMinutes, setMinimumGapMinutes] = useState(120);
  const [rounds, setRounds] = useState<Array<{ id: string; start: string }>>([]);
  const [pending, setPending] = useState<OfflineOperation>();
  const [pendingRebuild, setPendingRebuild] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const timezoneName = snapshot?.policy?.timezoneName ?? "Asia/Tehran";
  const effectiveFrom = useMemo(() => farmLocalIsoDate(new Date(), timezoneName), [timezoneName]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const next = await loadFeedingPolicyAdministration(supabase, farmId);
      setSnapshot(next);

      if (next.policy) {
        setPolicyName(next.policy.policyName);
        setActiveWeekdays(next.policy.activeWeekdays);
        setCompletionTargetMinutes(next.policy.completionTargetMinutes);
        setMinimumGapMinutes(next.policy.minimumGapMinutes);
        const starts =
          next.policy.roundStarts.length > 0
            ? next.policy.roundStarts
            : ["08:00", "10:00", "12:00", "14:00", "16:00"];
        setRounds(starts.map((start) => ({ id: crypto.randomUUID(), start })));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری برنامه تغذیه انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function toggleWeekday(value: number) {
    setActiveWeekdays((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value].sort((a, b) => a - b),
    );
  }

  function updateRound(id: string, value: string) {
    setRounds((current) =>
      current.map((round) => (round.id === id ? { ...round, start: value } : round)),
    );
  }

  function addRound() {
    setRounds((current) => [...current, { id: crypto.randomUUID(), start: "" }]);
  }

  function removeRound(id: string) {
    setRounds((current) => current.filter((round) => round.id !== id));
  }

  function preparePublication() {
    setError(undefined);
    setMessage(undefined);

    if (!snapshot?.canManage) {
      setError("دسترسی مدیریت تنظیمات سالن برای این حساب فعال نیست.");
      return;
    }

    try {
      const roundStarts = rounds.map((round) => round.start);
      const operation = buildFeedingPolicyPublishOperation(
        {
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          performedAt: new Date().toISOString(),
        },
        {
          policyName,
          activeWeekdays,
          completionTargetMinutes,
          minimumGapMinutes,
          roundStarts,
          effectiveFrom,
        },
      );

      const weekdayLabels = WEEKDAYS.filter((day) => activeWeekdays.includes(day.value))
        .map((day) => day.label)
        .join("، ");

      setPending(operation);
      setConfirmationParts([
        "انتشار نسخه جدید برنامه تغذیه",
        policyName,
        `روزها: ${weekdayLabels}`,
        `نوبت‌ها: ${roundStarts.join("، ")}`,
        `مهلت تکمیل: ${completionTargetMinutes} دقیقه`,
        `حداقل فاصله: ${minimumGapMinutes} دقیقه`,
        `اعمال از: ${jalaliFarmDate(effectiveFrom, timezoneName)}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "برنامه تغذیه معتبر نیست.");
    }
  }

  async function confirmPublication() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "انتشار برنامه تغذیه انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  function prepareTodayRebuild() {
    setError(undefined);
    setMessage(undefined);

    if (!snapshot?.canManage) {
      setError("دسترسی مدیریت تنظیمات سالن برای این حساب فعال نیست.");
      return;
    }

    try {
      setPendingRebuild(
        buildFeedingDayRebuildOperation(
          {
            actorId: userId,
            farmId,
            deviceId: getOrCreateDeviceId(),
            performedAt: new Date().toISOString(),
          },
          { localDate: effectiveFrom },
        ),
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "درخواست بازسازی برنامه امروز معتبر نیست.",
      );
    }
  }

  async function confirmTodayRebuild() {
    if (!pendingRebuild) return;

    try {
      setBusy(true);
      setError(undefined);
      setMessage(undefined);

      const result = await persistAndSyncOperation(
        supabase,
        pendingRebuild,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setPendingRebuild(undefined);

      if (result.operation.status === "terminal_conflict") {
        if (result.operation.lastErrorCode === "FEEDING_DAY_REBUILD_HAS_RECORDED_FEEDING") {
          setError(
            "امروز غذادهی واقعی ثبت شده است؛ برای حفاظت از تاریخچه، برنامه امروز بازسازی نشد.",
          );
        } else {
          setError(
            `بازسازی برنامه امروز متوقف شد: ${result.operation.lastErrorCode ?? "تعارض نامشخص"}`,
          );
        }
        return;
      }

      if (result.operation.status === "synced") {
        setMessage("برنامه امروز با آخرین نسخه تنظیمات بازسازی و همگام شد.");
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("discus:v2:feeding-schedule-changed"));
        }
        await refresh();
      } else if (result.operation.status === "pending") {
        setMessage("درخواست بازسازی برنامه امروز در صف آفلاین ثبت شد.");
      } else if (result.operation.status === "retryable_error") {
        setMessage("درخواست بازسازی حفظ شد و دوباره برای سرور ارسال می‌شود.");
      } else {
        setMessage("درخواست بازسازی برنامه امروز ثبت شد.");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بازسازی برنامه امروز انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (loading && !snapshot) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری برنامه تغذیه…</p>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="feeding-policy-admin-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4B1</p>
          <h2 id="feeding-policy-admin-title">برنامه تغذیه سالن</h2>
        </div>
        <span className="count-pill">
          {snapshot?.policy ? `نسخه ${snapshot.policy.versionNo}` : "بدون نسخه"}
        </span>
      </div>

      <p className="section-note">
        ذخیره تغییرات یک نسخه جدید می‌سازد؛ نسخه‌های تاریخی و نوبت‌های قبلاً آماده‌شده تغییر نمی‌کنند.
      </p>

      {snapshot?.policy && !snapshot.policy.scheduleUniform ? (
        <p className="settings-warning">
          زمان‌بندی نسخه فعلی بین روزها متفاوت است. انتشار از این فرم، زمان‌های زیر را برای همه روزهای
          فعال یکسان می‌کند.
        </p>
      ) : null}

      <div className="operation-field-grid">
        <label>
          نام برنامه
          <input value={policyName} onChange={(event) => setPolicyName(event.target.value)} />
        </label>

        <label>
          مهلت تکمیل هر نوبت (دقیقه)
          <input
            type="number"
            min="1"
            max="240"
            value={completionTargetMinutes}
            onChange={(event) => setCompletionTargetMinutes(Number(event.target.value))}
          />
        </label>

        <label>
          حداقل فاصله نوبت‌ها (دقیقه)
          <input
            type="number"
            min="1"
            max="720"
            value={minimumGapMinutes}
            onChange={(event) => setMinimumGapMinutes(Number(event.target.value))}
          />
        </label>
      </div>

      <div className="manager-card">
        <p className="section-kicker">روزهای فعال</p>
        <div className="operation-checks">
          {WEEKDAYS.map((day) => (
            <label key={day.value}>
              <input
                type="checkbox"
                checked={activeWeekdays.includes(day.value)}
                onChange={() => toggleWeekday(day.value)}
              />
              {day.label}
            </label>
          ))}
        </div>
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">نوبت‌ها</p>
            <h3>ساعت شروع</h3>
          </div>
          <button type="button" className="text-button" onClick={addRound}>
            افزودن نوبت
          </button>
        </div>

        <div className="operation-field-grid">
          {rounds.map((round, index) => (
            <label key={round.id}>
              نوبت {index + 1}
              <span className="confirm-actions">
                <input
                  type="time"
                  value={round.start}
                  onChange={(event) => updateRound(round.id, event.target.value)}
                />
                {rounds.length > 1 ? (
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => removeRound(round.id)}
                  >
                    حذف
                  </button>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      </div>

      <p className="section-note">
        اعمال از {jalaliFarmDate(effectiveFrom, timezoneName)} · منطقه زمانی {timezoneName}
      </p>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      <button
        type="button"
        className="secondary-button"
        disabled={busy || !snapshot?.canManage}
        onClick={preparePublication}
      >
        بررسی و انتشار نسخه جدید
      </button>

      <div className="manager-card">
        <p className="section-kicker">اعمال به امروز</p>
        <h3>بازسازی برنامه امروز</h3>
        <p className="section-note">
          اگر برنامه امروز قبلاً آماده شده باشد، این گزینه آن را با آخرین نسخه تنظیمات می‌سازد. اگر
          حتی یک غذادهی واقعی ثبت شده باشد، بازسازی برای حفاظت از تاریخچه متوقف می‌شود.
        </p>
        <button
          type="button"
          className="secondary-button"
          disabled={busy || !snapshot?.canManage || !snapshot?.policy}
          onClick={prepareTodayRebuild}
        >
          بررسی و بازسازی برنامه امروز
        </button>
      </div>

      {pending ? (
        <ConfirmationSheet
          title="تأیید برنامه تغذیه"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirmPublication()}
        />
      ) : null}

      {pendingRebuild ? (
        <ConfirmationSheet
          title="تأیید بازسازی برنامه امروز"
          parts={[
            "بازسازی برنامه غذادهی امروز با آخرین نسخه تنظیمات",
            `تاریخ سالن: ${jalaliFarmDate(effectiveFrom, timezoneName)}`,
            "اگر غذادهی واقعی ثبت شده باشد، عملیات بدون حذف تاریخچه متوقف می‌شود.",
          ]}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPendingRebuild(undefined)}
          onConfirm={() => void confirmTodayRebuild()}
        />
      ) : null}
    </section>
  );
}
